import React, { useState } from 'react';
import SimpleLoader from 'components/loader/SimpleLoader';
import { BackButton } from 'components/buttons';
import Header from 'components/header/Header';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';
import useCreateCluster, { NewClusterInput } from './hooks/useCreateCluster';
import useListEnvironments from 'views/Datasets/hooks/useListEnvironments';
import { NButton, NInput, NSelect } from '@innersource-nn/novoui-react';
import AutocompleteTooltip, {
    Option,
} from 'components/Inputs/InputsWithTooltip/AutocompleteTooltip';
import { EnvironmentSelectItem } from 'types/Environment';
import NSelectTooltip from 'components/Inputs/InputsWithTooltip/NSelectTooltip';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import { validateInput } from 'components/Inputs/regexValidation';

const nodeTypes = [
    { label: 'dc2.large', name: 'dc2.large' },
    { label: 'ds2.xlarge', name: 'ds2.xlarge' },
    { label: 'ds2.8xlarge', name: 'ds2.8xlarge' },
    { label: 'dc1.large', name: 'dc1.large' },
    { label: 'dc2.8xlarge', name: 'dc2.8xlarge' },
    { label: 'ra3.16xlarge', name: 'ra3.16xlarge' },
];

const filter = {
    roles: ['Admin', 'Owner', 'Invited', 'DatasetCreator'],
    page: 1,
    pageSize: 20,
    samlgroup: ['admin', 'fulldev', 'basicdev'],
};
const WarehouseForm = () => {
    const [selectedEnvUri, setSelectedEnvUri] = useState('');
    const [formData, setFormData] = useState<NewClusterInput>({
        label: '',
        description: '',
        nodeType: '',
        numberOfNodes: '2',
        masterDatabaseName: '',
        databaseName: '',
        vpc: '',
        authorized_resources: '',
        tags: [],
        SamlGroupName: '',
    });

    const { buttonLoading, handleSubmit } = useCreateCluster();
    const { environments, environmentError, rolesError, loading, roles } =
        useListEnvironments(selectedEnvUri, filter);

    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };

    function handleSelectEnvironment(selectedEnvironment: Option) {
        //implement the logic to handle the selected environment as in handlechange
        const { key } = selectedEnvironment as EnvironmentSelectItem;
        setSelectedEnvUri(key);
        setFormData({
            ...formData,
            SamlGroupName: '',
        });
    }

    const handleChange = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const { name, value } = event.target;
        if (name === 'environment') {
            const selectedEnvironment = environments.find(
                (option) => option.key === value,
            );

            if (selectedEnvironment) {
                setSelectedEnvUri(selectedEnvironment.key);
                setFormData({
                    ...formData,
                    SamlGroupName: '',
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };
    const isButtonDisabled = () => {
        const { label, description, nodeType, masterDatabaseName, vpc } =
            formData;
        return (
            label === '' ||
            description === '' ||
            nodeType === '' ||
            masterDatabaseName === '' ||
            vpc === ''
        );
    };

    return (
        <div>
            <BackButton />
            <Header title="Create Redshift Cluster" />
            {loading ? (
                <SimpleLoader />
            ) : (
                <div className="flex flex-col gap-16 info-container">
                    <NInput
                        label="Warehouse name*"
                        name="label"
                        placeholder="Please insert a short title which describes this warehouse..."
                        variant="outline"
                        modelValue={formData.label}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                label: e.target.value,
                            })
                        }
                    />
                    <NTextAreaQuill
                        label="Warehouse description*"
                        variant="outline"
                        placeholder="Please insert a short description which describes what this warehouse contains and the purpose of the pipeline..."
                        modelValue={formData.description}
                        onChange={(value) =>
                            setFormData({
                                ...formData,
                                description: value,
                            })
                        }
                    />
                    <div className="grid grid-cols-3 gap-16 mt-16">
                        <AutocompleteTooltip
                            label="Environment *"
                            placeholder="Search for environment...."
                            tooltip="Select the environment where the dataset will be deplayed in."
                            items={environments}
                            handleSelected={handleSelectEnvironment}
                            error={environmentError}
                        />
                        <NSelectTooltip
                            content="Select the admin group responsible for managing permissions and access controls."
                            label="Admin group*"
                            size="medium"
                            variant="outline"
                            name="SamlGroupName"
                            options={roles}
                            disabled={
                                (roles.length === 0 &&
                                    formData.SamlGroupName === '') ||
                                environmentError !== ''
                            }
                            modelValue={formData.SamlGroupName}
                            onChange={handleChange}
                            placeholder="Select group"
                            invalid={rolesError !== ''}
                            assistiveText={rolesError}
                        />
                        <NInput
                            label="VPC ID *"
                            placeholder="VPC should contain at least one private subnet"
                            variant="outline"
                            modelValue={formData.vpc}
                            invalid={
                                !validateInput(
                                    formData.vpc,
                                    /^vpc-[a-z0-9]{17}$/,
                                )
                            }
                            assistiveText={
                                validateInput(
                                    formData.vpc,
                                    /^vpc-[a-z0-9]{17}$/,
                                )
                                    ? ''
                                    : 'vpc: Value does not match regex ^vpc-[a-z0-9]{17}$"'
                            }
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    vpc: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Master Database *"
                            variant="outline"
                            modelValue={formData.masterDatabaseName}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    masterDatabaseName: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Database Name (DB used by Datahub)"
                            variant="outline"
                            modelValue={formData.databaseName}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    databaseName: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Authorized Resources List"
                            placeholder="sg-123456,0.0.0.0/0,sg-654321"
                            variant="outline"
                            modelValue={formData.authorized_resources}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    authorized_resources: e.target.value,
                                })
                            }
                        />
                        <NSelect
                            label="Node Type *"
                            size="medium"
                            variant="outline"
                            options={nodeTypes}
                            modelValue={formData.nodeType}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    nodeType: e.target.value,
                                })
                            }
                        />
                        <NInput
                            label="Number of Nodes *"
                            variant="outline"
                            strictNumeric={true}
                            modelValue={formData.numberOfNodes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    numberOfNodes: e.target.value,
                                })
                            }
                        />
                        <NInputWithChips
                            label="Tags"
                            name="tags"
                            content="Add tags for the warehouse"
                            labels={formData.tags ?? []}
                            updateLabels={updateLabels}
                            modelValue=""
                        />
                    </div>
                    <div className="flex justify-end">
                        <NButton
                            variant="fill"
                            square
                            onClick={() =>
                                handleSubmit(selectedEnvUri, formData)
                            }
                            isLoading={buttonLoading}
                            disabled={isButtonDisabled()}
                        >
                            Create warehouse
                        </NButton>
                    </div>
                </div>
            )}
        </div>
    );
};
export default WarehouseForm;
